import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types';

// Query keys
export const inventoryKeys = {
  all: ['inventory'] as const,
  products: () => [...inventoryKeys.all, 'products'] as const,
  product: (id: string) => [...inventoryKeys.products(), id] as const,
  customers: () => [...inventoryKeys.all, 'customers'] as const,
  customer: (id: string) => [...inventoryKeys.customers(), id] as const,
  vendors: () => [...inventoryKeys.all, 'vendors'] as const,
  vendor: (id: string) => [...inventoryKeys.vendors(), id] as const,
  invoices: () => [...inventoryKeys.all, 'invoices'] as const,
  invoiceCount: (type: 'sale' | 'purchase') => [...inventoryKeys.invoices(), 'count', type] as const,
};

// Hook to fetch products with caching
export const useProducts = () => {
  return useQuery({
    queryKey: inventoryKeys.products(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('organization_id', org.id)
        .order('name')
        .range(0, 9999);

      if (error) throw error;
      return data as unknown as Product[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

// Hook to fetch customers with caching
export const useCustomers = () => {
  return useQuery({
    queryKey: inventoryKeys.customers(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('organization_id', org.id)
        .order('name')
        .range(0, 9999);

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to fetch vendors with caching
export const useVendors = () => {
  return useQuery({
    queryKey: inventoryKeys.vendors(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('organization_id', org.id)
        .order('name')
        .range(0, 9999);

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to get optimized invoice number
export const useInvoiceNumber = (type: 'sale' | 'purchase') => {
  return useQuery({
    queryKey: inventoryKeys.invoiceCount(type),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      // Get only the count, more efficient than selecting all
      const { count } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', org.id)
        .eq('type', type);

      const invoiceNum = (count || 0) + 1;
      const prefix = type === 'sale' ? 'INV' : 'PUR';
      const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
      
      return `${prefix}-${dateStr}-${String(invoiceNum).padStart(3, '0')}`;
    },
    staleTime: 0, // Always fresh for invoice numbers
    gcTime: 0, // Don't cache
  });
};
